import React from "react"
import { Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap"
import CFButton from "../../../components/CFButton/CFButton"
import { useGeocodingCore } from "@mapbox/search-js-react"

import { AddressMinimap } from "@mapbox/search-js-react"

import "./ModalMinimapAddress.scss"
import ModalNumberAddress from "../ModalNumberAddress/ModalNumberAddress"
import CompanyContext from "../../../contexts/CompanyContext"
import SettingsContext from "../../../contexts/SettingsContext"
import { formatBoolean } from "../../../helpers/GeneralTools"

const MAPBOX_KEY = process.env.REACT_APP_MAPBOX_KEY
const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_KEY

const ModalMinimapAddress = ({ status, content, onClose, onConfirmLocation, setModalMinimapAddress, config }) => {
  const { company } = React.useContext(CompanyContext)
  const { settings } = React.useContext(SettingsContext)

  const [locationSaved, setLocationSaved] = React.useState(null)
  const [mapLoading, setMapLoading] = React.useState(true)

  const [userLocation, setUserLocation] = React.useState({ latitude: null, longitude: null })
  const [locationError, setLocationError] = React.useState(null)
  const [freePin, setFreePin] = React.useState(false)

  // Configurações do provedor
  const defaultConfig = {
    provider: settings.map_provider || "mapbox",
    mapboxToken: MAPBOX_KEY,
    googleApiKey: GOOGLE_KEY,
    ...config
  }

  const geocodingCore = useGeocodingCore({ accessToken: MAPBOX_KEY })

  React.useEffect(() => {
    if (status && !!content) {
      setTimeout(() => {
        const map = document.getElementsByTagName("mapbox-address-minimap")

        if (!!map) {
          const mapSeed = map[0].getAttribute("data-seed")

          const adjustButton = document.getElementsByClassName(`${mapSeed}--MinimapButtonAdjust`)
          const cancelButton = document.getElementsByClassName(`${mapSeed}--MinimapButtonCancel`)
          const saveButton = document.getElementsByClassName(`${mapSeed}--MinimapButtonSave`)
          const mapboxLogo = document.getElementsByClassName(`${mapSeed}--MinimapAttributionLogo`)
          const mapboxText = document.getElementsByClassName(`${mapSeed}--MinimapAttributionText`)
          const modalBody = document.getElementsByClassName(`modal-minimap-address--body`)
          const x = document.getElementsByClassName(`${mapSeed}--MinimapImageContainer`)
          const x2 = document.getElementsByClassName(`${mapSeed}--MinimapInnerFrame`)
          const x3 = document.getElementsByClassName(`${mapSeed}--MapboxAddressMinimap`)
          const x4 = document.getElementsByClassName(`${mapSeed}--MinimapImageContainer`)

          if (!!adjustButton) {
            adjustButton[0].style.display = "none"
            adjustButton[0].click()
          }

          if (!!cancelButton) {
            cancelButton[0].style.display = "none"
          }

          if (!!saveButton) {
            saveButton[0].style.display = "none"
          }

          if (!!mapboxLogo) {
            mapboxLogo[0].style.display = "none"
          }

          if (!!mapboxText) {
            mapboxText[0].style.display = "none"
          }
          if (!!x && !!modalBody) {
            x[0].style.width = `${map[0].getBoundingClientRect().width}px`
            x[0].style.height = `${modalBody[0].getBoundingClientRect().height - 32 - 42 - 20}px`
          }

          if (!!x3 && !!modalBody) {
            x3[0].style.width = `${map[0].getBoundingClientRect().width}px`
            x3[0].style.height = `${modalBody[0].getBoundingClientRect().height - 32 - 42 - 20}px`
          }

          if (!!x2) {
            x2[0].style.width = `${map[0].getBoundingClientRect().width}px`
            x2[0].style.height = `${modalBody[0].getBoundingClientRect().height - 32 - 42 - 20}px`
          }

          setMapLoading(false)
        }
      }, 100)
    } else if (status && !content) {
      setMapLoading(false)
    }
  }, [status, content, freePin])

  React.useEffect(() => {
    if (!!locationSaved && content.feature && status) {
      const streetData = getStreetData(
        content.feature,
        locationSaved[1],
        locationSaved[0],
        formatBoolean(content.withoutNumber) ? "" : !!content?.feature?.properties?.context?.address?.address_number ? content?.feature?.properties?.context?.address?.address_number : ""
      )

      onSaveLocation({ ...streetData, withoutNumber: content.withoutNumber })
    }
  }, [locationSaved])

  React.useEffect(async () => {
    if (!!userLocation.latitude && !!userLocation.longitude) {
      try {
        let geocodingResult = null

        if (defaultConfig.provider === "mapbox") {
          // Usar Mapbox Geocoding
          const response = await geocodingCore.reverse(
            { lat: userLocation.latitude, lon: userLocation.longitude },
            {
              country: "BR",
              limit: 3,
              language: "pt-br",
              proximity: company?.company_address[0]?.geo_coordinates ?
                [company.company_address[0].geo_coordinates[1], company.company_address[0].geo_coordinates[0]] :
                undefined,
              types: ["street", "address"],
            }
          )

          if (response?.features?.[0]) {
            geocodingResult = {
              latitude: userLocation.latitude,
              longitude: userLocation.longitude,
              feature: response.features[0]
            }
          }
        } else if (defaultConfig.provider === "google") {
          // Usar Google Geocoding
          await loadGoogleMapsAPI()

          if (window.google && window.google.maps) {
            const geocoder = new window.google.maps.Geocoder()

            geocoder.geocode(
              {
                location: { lat: userLocation.latitude, lng: userLocation.longitude },
                language: "pt-BR",
                region: "BR"
              },
              (results, status) => {
                if (status === "OK" && results?.[0]) {
                  // Converter resultado do Google para formato compatível
                  const googleResult = results[0]
                  const feature = convertGoogleToMapboxFormat(googleResult, userLocation.latitude, userLocation.longitude)

                  setModalMinimapAddress({
                    status: true,
                    content: {
                      latitude: userLocation.latitude,
                      longitude: userLocation.longitude,
                      feature: feature
                    },
                  })
                } else {
                  console.error("Erro no geocoding do Google:", status)
                  setLocationError("Não foi possível encontrar o endereço da sua localização.")
                  setMapLoading(false)
                }
              }
            )
          }
        }

        // Para Mapbox, definir o resultado diretamente
        if (defaultConfig.provider === "mapbox" && geocodingResult) {
          setModalMinimapAddress({
            status: true,
            content: geocodingResult,
          })
        }
      } catch (error) {
        console.error("Erro no geocoding:", error)
        setLocationError("Não foi possível encontrar o endereço da sua localização.")
        setMapLoading(false)
      }
    }
  }, [userLocation])

  // Carregar Google Maps API
  const loadGoogleMapsAPI = () => {
    if (window.google && window.google.maps) {
      return Promise.resolve()
    }

    return new Promise((resolve) => {
      const script = document.createElement('script')
      script.src = `https://maps.googleapis.com/maps/api/js?key=${defaultConfig.googleApiKey}&libraries=geometry&language=pt&region=BR`
      script.async = true
      script.defer = true
      script.onload = resolve
      document.head.appendChild(script)
    })
  }

  // Converter resultado do Google para formato compatível com Mapbox
  const convertGoogleToMapboxFormat = (googleResult, latitude, longitude) => {
    const addressComponents = googleResult.address_components || []

    const getComponent = (type) => {
      const component = addressComponents.find(comp => comp.types.includes(type))
      return component?.long_name || null
    }

    // Simular estrutura do Mapbox
    return {
      properties: {
        context: {
          street: {
            name: getComponent('route')
          },
          neighborhood: {
            name: getComponent('sublocality_level_1') || getComponent('neighborhood')
          },
          postcode: {
            name: getComponent('postal_code')
          },
          place: {
            name: getComponent('locality') || getComponent('administrative_area_level_2')
          },
          region: {
            name: getComponent('administrative_area_level_1')
          },
          address: {
            address_number: getComponent('street_number')
          }
        },
        full_address: googleResult.formatted_address
      },
      geometry: {
        coordinates: [longitude, latitude]
      }
    }
  }

  const getStreetData = (feature, latitude, longitude, number) => {
    return {
      street: feature?.properties?.context?.street?.name,
      neighbourhood: feature?.properties?.context?.neighborhood?.name,
      zipcode:
        feature?.properties?.context?.postcode?.name?.length === 9
          ? feature?.properties?.context?.postcode?.name
          : (feature?.properties?.context?.postcode?.name || "") + "-000",
      city: feature?.properties?.context?.place?.name,
      state: feature?.properties?.context?.region?.name,
      coordinates: { latitude, longitude },
      number: number,
    }
  }

  const onSaveLocation = (streetData) => {
    onConfirmLocation(streetData)
    onClose()
  }

  const getUserLocation = () => {
    setMapLoading(true)
    setLocationError(null)

    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        async function (position) {
          setUserLocation({ latitude: position.coords.latitude, longitude: position.coords.longitude })
        },
        (error) => {
          if (error.code === 1) {
            setLocationError("Permissão de localização negada, por favor permitir que o seu navegador acesse a sua localização.")
            setMapLoading(false)
          } else if (error.code === 2) {
            setLocationError("Infelizmente sua posição nesse momento está indisponível.")
            setMapLoading(false)
          } else {
            setLocationError("Não conseguimos identificar sua localização")
            setMapLoading(false)
          }
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000
        }
      )
    } else {
      console.warn("Localização não disponível")
      setLocationError("Infelizmente não conseguimos encontrar sua localização.")
      setMapLoading(false)
    }
  }

  if (status) {
    return (
      <Modal
        className="modal-minimap-address"
        isOpen={status}
        toggle={() => {
          onClose()
          setFreePin(false)
        }}
        backdrop={"static"}
        fade={false}
        fullscreen={true}
        keyboard={false}
      >
        <div className={`modal-minimap-address--overlay ${freePin ? "show" : ""}`}>
          <p>Arraste o marcador para ajustar a localização. Você pode mover o mapa e o marcador para encontrar o endereço correto.</p>
        </div>
        <ModalHeader className="modal-minimap-address--header">{!!content ? "Ajustar localização" : "Liberar localização"}</ModalHeader>
        <ModalBody className="modal-minimap-address--body" onDragStart={(e) => console.log(e)} onDragEnd={(e) => console.log(e)}>
          <div className="modal-minimap-address--body--top">
            {!!content ? (
              <p>
                Marque no mapa onde você encontrará o entregador.
                <br />
                Ex: Portão ou entrada do condomínio.
              </p>
            ) : (
              <p>Libere sua localização para podermos encontrar seu endereço!</p>
            )}
          </div>
          {!!content ? (
            <div className="modal-minimap-address--body--map">
              {mapLoading && (
                <div className="modal-minimap-address--body--map--overlay">
                  <Spinner />
                  <p>Carregando o mapa</p>
                </div>
              )}
              {freePin ? (
                <>
                  <AddressMinimap
                    adjustBtnText="Ajustar localização"
                    cancelBtnText="Cancelar"
                    saveBtnText="Salvar localização"
                    accessToken={MAPBOX_KEY}
                    show={true}
                    footer={false}
                    canAdjustMarker={true}
                    onSaveMarkerLocation={(e) => {
                      setLocationSaved(e)
                    }}
                    feature={content.feature}
                    keepMarkerCentered={false}
                    theme={{
                      variables: {
                        colorPrimary: "#DF3B45",
                      },
                    }}
                  />
                </>
              ) : (
                <AddressMinimap
                  adjustBtnText="Ajustar localização"
                  cancelBtnText="Cancelar"
                  saveBtnText="Salvar localização"
                  accessToken={MAPBOX_KEY}
                  show={true}
                  footer={false}
                  canAdjustMarker={true}
                  onSaveMarkerLocation={(e) => {
                    setLocationSaved(e)
                  }}
                  feature={content.feature}
                  keepMarkerCentered={true}
                  theme={{
                    variables: {
                      colorPrimary: "#DF3B45",
                    },
                  }}
                />
              )}
            </div>
          ) : (
            <div className="modal-minimap-address--body--location">
              <div className="modal-minimap-address--body--location-icon">
                <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22S19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9S10.62 6.5 12 6.5S14.5 7.62 14.5 9S13.38 11.5 12 11.5Z"
                    fill={locationError ? "#FF6B6B" : "#DF3B45"}
                  />
                </svg>
              </div>

              <div className="modal-minimap-address--body--location-content">
                <h3>{locationError ? "Ops! Algo deu errado" : "Precisamos da sua localização"}</h3>
                <p>
                  {locationError
                    ? locationError
                    : "Para encontrarmos o endereço mais próximo de você, precisamos acessar sua localização atual."
                  }
                </p>
              </div>

              <div className="modal-minimap-address--body--location-action">
                <CFButton
                  title={locationError ? "Tentar novamente" : "Permitir localização"}
                  icon={locationError ? "refresh" : "location"}
                  loading={mapLoading}
                  loadingText="Obtendo localização..."
                  onClick={getUserLocation}
                  size="large"
                />

                {!locationError && (
                  <div className="modal-minimap-address--body--location-help">
                    <p>
                      <strong>Por que precisamos disso?</strong><br />
                      Sua localização nos ajuda a encontrar endereços próximos e melhorar a precisão da entrega.
                    </p>
                  </div>
                )}

                {locationError && (
                  <div className="modal-minimap-address--body--location-troubleshoot">
                    <p><strong>Problemas com a localização?</strong></p>
                    <ul>
                      <li>Verifique se o GPS está ativado</li>
                      <li>Permita o acesso à localização no navegador</li>
                      <li>Tente recarregar a página</li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter className="modal-minimap-address--footer">
          <div className="modal-minimap-address--footer--content">
            {!!content && (
              <CFButton title={freePin ? "Centralizar marcador" : "Liberar marcador"} onClick={() => setFreePin(!freePin)} outline={true} />
            )}
            <div className="modal-minimap-address--footer--flex">
              <CFButton
                title="Voltar"
                onClick={() => {
                  onClose()
                  setFreePin(false)
                }}
                outline={true}
              />
              {!!content && (
                <CFButton
                  title="Confirmar Localização"
                  onClick={() => {
                    if (!!content) {
                      const map = document.getElementsByTagName("mapbox-address-minimap")

                      if (!!map) {
                        const mapSeed = map[0].getAttribute("data-seed")

                        const saveButton = document.getElementsByClassName(`${mapSeed}--MinimapButtonSave`)
                        const adjustButton = document.getElementsByClassName(`${mapSeed}--MinimapButtonAdjust`)

                        if (!!saveButton) {
                          saveButton[0].click()
                        }

                        if (!!adjustButton) {
                          adjustButton[0].click()
                        }
                      }
                    }
                  }}
                />
              )}
            </div>
          </div>
        </ModalFooter>
      </Modal>
    )
  }

  return ""
}

export default ModalMinimapAddress